import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import {
  ParkingSquare, Loader2, Car, MapPin, Phone, Clock, ShieldCheck,
  CreditCard, Banknote, CheckCircle2, BellRing, AlertTriangle, Navigation,
  ScanLine, Timer, Receipt,
} from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { playSiren, unlockAudio } from "@/lib/alertSound";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

import { seqDeFolio } from "@/domain/valet";

// Contrato de adhesión por defecto (leyenda base PROFECO) cuando la sucursal no configuró uno.
const CONTRATO_DEFAULT = `CONTRATO DE ADHESIÓN Y CONDICIONES DEL SERVICIO DE ESTACIONAMIENTO

1. El presente boleto ampara únicamente el uso del cajón de estacionamiento por el tiempo de permanencia del vehículo. La tarifa se calcula conforme a las tarifas exhibidas en el establecimiento.
2. El establecimiento no se hace responsable por objetos de valor dejados dentro del vehículo. Se recomienda no dejar objetos a la vista.
3. En caso de extravío del boleto, el usuario deberá acreditar la propiedad del vehículo (tarjeta de circulación e identificación oficial) y cubrir la tarifa por boleto perdido vigente.
4. El horario de servicio es el exhibido en el establecimiento. El usuario deberá retirar su vehículo dentro de dicho horario.
5. Cualquier reclamación por daños deberá presentarse antes de retirar el vehículo del establecimiento.
6. El usuario acepta las presentes condiciones al hacer uso del servicio, de conformidad con la Ley Federal de Protección al Consumidor (PROFECO).`;

// Estatus públicos (anonimizados) que devuelve el backend para VALET.
const STATUS = {
  EN_RESGUARDO: {
    label: "En resguardo",
    color: "#3B82F6",
    icon: ShieldCheck,
    desc: "Tu vehículo está seguro con nosotros.",
  },
  SOLICITADO: {
    label: "En camino",
    color: "#F59E0B",
    icon: Car,
    desc: "Nuestro equipo está trayendo tu auto a la bahía.",
  },
  LISTO_EN_BAHIA: {
    label: "Listo en bahía",
    color: "#10B981",
    icon: CheckCircle2,
    desc: "¡Tu auto te espera! Acércate a la bahía de salida.",
  },
  ENTREGADO: {
    label: "Entregado",
    color: "#6B7280",
    icon: CheckCircle2,
    desc: "Gracias por tu visita. ¡Buen viaje!",
  },
};

const fmtMMSS = (ms) => {
  const t = Math.max(0, Math.floor(ms / 1000));
  const m = String(Math.floor(t / 60)).padStart(2, "0");
  const s = String(t % 60).padStart(2, "0");
  return `${m}:${s}`;
};

// Tiempo transcurrido "01h 45m" a partir de una fecha ISO
const fmtTranscurrido = (ms) => {
  const total = Math.max(0, Math.floor(ms / 60000));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m`;
};

const fmtMoneda = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n || 0));

// ============================= Bloques compartidos =============================

function Header({ suc, subtitulo }) {
  return (
    <header className="px-6 pt-8 pb-5 flex flex-col items-center text-center border-b border-white/10">
      {suc.logo_base64 ? (
        <img src={suc.logo_base64} alt={suc.nombre} className="h-16 max-w-[180px] object-contain mb-3" data-testid="landing-logo" />
      ) : (
        <div className="h-12 w-12 bg-[#FFCC00] flex items-center justify-center mb-3">
          <ParkingSquare className="h-7 w-7 text-[#0A0A0A]" strokeWidth={2.5} />
        </div>
      )}
      <p className="font-display font-black text-lg tracking-tight leading-none" data-testid="landing-sucursal-nombre">{suc.nombre || "Estacionamiento"}</p>
      <p className="text-[10px] tracking-[0.25em] text-white/40 font-bold mt-2 uppercase">{subtitulo}</p>
    </header>
  );
}

function UbicacionYContacto({ suc }) {
  const mapsUrl = suc.google_maps_url
    || (suc.direccion_completa
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([suc.direccion_completa, suc.nombre].filter(Boolean).join(" "))}`
      : null);
  if (!(suc.direccion_completa || suc.telefono_oficinas || suc.horario_atencion || suc.horario)) return null;
  return (
    <section className="border border-white/10 bg-white/5 p-4 space-y-3" data-testid="landing-ubicacion">
      <p className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-bold">Ubicación y horarios de atención</p>
      {suc.direccion_completa && (
        <div className="flex items-start gap-3">
          <MapPin className="h-5 w-5 text-[#FFCC00] shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-white/80">{suc.direccion_completa}</p>
            {mapsUrl && (
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="landing-maps-link"
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#FFCC00] mt-1.5 hover:underline">
                <Navigation className="h-3.5 w-3.5" /> Cómo llegar en Google Maps
              </a>
            )}
          </div>
        </div>
      )}
      {(suc.horario_atencion || suc.horario) && (
        <div className="flex items-center gap-3" data-testid="landing-horario">
          <Clock className="h-5 w-5 text-white/40 shrink-0" />
          <p className="text-sm text-white/70">{suc.horario_atencion || suc.horario}</p>
        </div>
      )}
      {suc.telefono_oficinas && (
        <a href={`tel:${suc.telefono_oficinas}`} data-testid="landing-tel-link" className="flex items-center gap-3 hover:text-[#FFCC00]">
          <Phone className="h-5 w-5 text-white/40 shrink-0" />
          <span className="text-sm text-white/70">{suc.telefono_oficinas}</span>
        </a>
      )}
    </section>
  );
}

function ContratoAdhesion({ texto }) {
  const contenido = texto && texto.trim() ? texto : CONTRATO_DEFAULT;
  return (
    <Accordion type="single" collapsible className="border border-white/10 bg-white/5 px-4" data-testid="landing-contrato">
      <AccordionItem value="contrato" className="border-0">
        <AccordionTrigger data-testid="landing-contrato-trigger" className="text-xs font-bold uppercase tracking-[0.15em] text-white/60 hover:no-underline hover:text-white">
          <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#FFCC00]" /> Contrato de adhesión y políticas de servicio</span>
        </AccordionTrigger>
        <AccordionContent className="text-[11px] leading-relaxed text-white/50 whitespace-pre-line pb-4" data-testid="landing-contrato-texto">
          {contenido}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

// ============================= Vista: Estacionamiento Público =============================

function VistaAutoservicio({ data }) {
  const suc = data?.sucursal || {};
  const finalizado = data?.estado === "FINALIZADO";
  const pagado = data?.estado === "PAGADO";
  const tarifas = Array.isArray(data?.tarifas) ? data.tarifas : [];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col" data-testid="landing-root" data-service="ESTACIONAMIENTO_PUBLICO">
      <Header suc={suc} subtitulo="Estacionamiento · Autoservicio" />

      <main className="flex-1 w-full max-w-md mx-auto px-6 py-7 flex flex-col gap-6">
        {/* Turno (estética Valet) */}
        <section className="text-center">
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">Tu boleto</p>
          <p className="font-display font-black text-[#FFCC00] leading-none" style={{ fontSize: "5rem" }} data-testid="autoservicio-secuencial">
            {seqDeFolio(data?.folio)}
          </p>
          <p className="text-xs font-mono text-white/50 mt-1" data-testid="autoservicio-folio">{data?.folio}</p>
          {data?.placa && (
            <p className="text-sm font-mono text-white/70 mt-1" data-testid="autoservicio-placa">Placa: <b className="text-white">{data.placa}</b></p>
          )}
        </section>

        {/* Aviso de pago destacado */}
        {finalizado ? (
          <section className="border border-white/15 bg-white/5 p-5 text-center" data-testid="autoservicio-estado">
            <CheckCircle2 className="h-10 w-10 text-white/50 mx-auto" />
            <p className="font-display font-black text-xl text-white/80 mt-2">Estancia finalizada</p>
            <p className="text-white/50 text-sm mt-1">Gracias por tu visita.</p>
          </section>
        ) : pagado ? (
          <section className="border border-[#10B981]/40 bg-[#10B981]/10 p-5 text-center" data-testid="autoservicio-estado">
            <CheckCircle2 className="h-10 w-10 text-[#10B981] mx-auto" />
            <p className="font-display font-black text-xl text-[#10B981] mt-2">Ticket pagado</p>
            <p className="text-white/60 text-sm mt-1">Puedes salir. Presenta tu boleto en la caseta.</p>
          </section>
        ) : (
          <section className="border-2 border-[#FFCC00]/60 bg-[#FFCC00]/10 p-5 text-center" data-testid="autoservicio-aviso-pago">
            <CreditCard className="h-8 w-8 text-[#FFCC00] mx-auto" />
            <p className="font-display font-black text-lg text-[#FFCC00] mt-2 leading-tight">💳 Favor de pasar a pagar a caseta para validar su salida</p>
          </section>
        )}

        {/* Tarifas por hora / fracción */}
        {tarifas.length > 0 && (
          <section className="border border-white/10 bg-white/5 p-4" data-testid="autoservicio-tarifas">
            <p className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-bold mb-3 flex items-center gap-2">
              <Receipt className="h-4 w-4 text-[#FFCC00]" /> Costo por hora o fracción
            </p>
            <div className="divide-y divide-white/10">
              {tarifas.map((t) => (
                <div key={t.tipo_vehiculo} className="flex items-center justify-between py-2.5" data-testid={`autoservicio-tarifa-${String(t.tipo_vehiculo).toLowerCase()}`}>
                  <span className="text-sm font-bold text-white/80">{t.tipo_vehiculo}</span>
                  <span className="font-display font-black text-lg text-[#FFCC00]">{fmtMoneda(t.precio)}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-white/40 mt-3 leading-relaxed">Tarifa por hora o fracción. El monto final se calcula en caja según tu tiempo de estancia.</p>
          </section>
        )}

        {/* Código QR del folio para escaneo en caseta */}
        {!finalizado && (
          <section className="border border-[#FFCC00]/40 bg-white/5 p-5 flex flex-col items-center text-center" data-testid="autoservicio-qr-box">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#FFCC00] font-bold flex items-center gap-2">
              <ScanLine className="h-4 w-4" /> Tu código de salida
            </p>
            <div className="bg-white p-4 mt-4 rounded-sm" data-testid="autoservicio-qr">
              <QRCodeCanvas
                value={data?.folio || ""}
                size={220}
                level="H"
                bgColor="#FFFFFF"
                fgColor="#000000"
                includeMargin={false}
              />
            </div>
            <p className="text-white/60 text-sm mt-4 leading-relaxed max-w-xs">
              Presenta este código QR en la caseta de cobro / salida para agilizar tu escaneo.
            </p>
          </section>
        )}

        {/* Ten listo tu pago */}
        {!finalizado && !pagado && (
          <section className="border border-white/10 bg-white/5 p-4" data-testid="landing-pago-aviso">
            <p className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-bold mb-3">Ten listo tu pago</p>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-2 text-sm font-bold text-white/80">
                <Banknote className="h-5 w-5 text-[#10B981]" /> Efectivo
              </span>
              {suc.acepta_tarjeta ? (
                <span className="inline-flex items-center gap-2 text-sm font-bold text-white/80">
                  <CreditCard className="h-5 w-5 text-[#3B82F6]" /> Tarjeta
                </span>
              ) : (
                <span className="text-xs text-[#F59E0B] font-bold uppercase tracking-wider">Solo efectivo</span>
              )}
            </div>
          </section>
        )}

        <UbicacionYContacto suc={suc} />
        <ContratoAdhesion texto={data?.contrato_adhesion} />
      </main>

      <footer className="px-6 py-5 text-center border-t border-white/10">
        <p className="text-[10px] tracking-[0.2em] text-white/25 font-bold uppercase">Estacionamiento · Actualización automática</p>
      </footer>
    </div>
  );
}

// ============================= Vista: Valet Parking =============================

function VistaValet({ data, folio, onSolicitar, solicitando, remaining, deadline, onConfirmar, confirmando, confirmado }) {
  const suc = data?.sucursal || {};
  const [aceptado, setAceptado] = useState(false);
  const st = STATUS[data?.estatus] || STATUS.EN_RESGUARDO;
  const StIcon = st.icon;
  const puedeSolicitar = data?.estatus === "EN_RESGUARDO";
  const enCamino = data?.estatus === "SOLICITADO";
  const listo = data?.estatus === "LISTO_EN_BAHIA";
  const entregado = data?.estatus === "ENTREGADO";
  const tiempoAgotado = enCamino && deadline && remaining <= 0;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col" data-testid="landing-root" data-service="VALET_PARKING">
      <Header suc={suc} subtitulo="Servicio de Valet Parking" />

      <main className="flex-1 w-full max-w-md mx-auto px-6 py-7 flex flex-col gap-6">
        <section className="text-center">
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">Tu turno</p>
          <p className="font-display font-black text-[#FFCC00] leading-none" style={{ fontSize: "5rem" }} data-testid="landing-secuencial">
            {seqDeFolio(data?.folio)}
          </p>
          <p className="text-xs font-mono text-white/50 mt-1" data-testid="landing-folio">{data?.folio}</p>

          <motion.div
            key={data?.estatus}
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="mt-5 inline-flex items-center gap-2.5 px-4 py-2.5 border font-bold uppercase tracking-wider text-sm"
            style={{ color: st.color, borderColor: `${st.color}66`, backgroundColor: `${st.color}1A` }}
            data-testid="landing-status-badge" data-status={data?.estatus}
          >
            <StIcon className={`h-5 w-5 ${enCamino ? "animate-pulse" : ""}`} />
            {st.label}
          </motion.div>
          <p className="text-white/60 text-sm mt-3 leading-relaxed" data-testid="landing-status-desc">{st.desc}</p>
        </section>

        <section className="flex flex-col items-center gap-4">
          <AnimatePresence mode="wait">
            {puedeSolicitar && (
              <motion.button
                key="btn-solicitar"
                initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                onClick={onSolicitar} disabled={solicitando} data-testid="landing-solicitar-btn"
                className="btn-hard w-full h-16 bg-[#FFCC00] text-[#0A0A0A] border border-[#FFCC00] font-black uppercase tracking-wider text-lg flex items-center justify-center gap-3 disabled:opacity-60"
              >
                {solicitando ? <Loader2 className="h-6 w-6 animate-spin" /> : <><BellRing className="h-6 w-6" /> Solicitar mi auto</>}
              </motion.button>
            )}

            {enCamino && (
              <motion.div
                key="timer"
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="w-full border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-5 text-center" data-testid="landing-timer-box"
              >
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#F59E0B] font-bold flex items-center justify-center gap-2">
                  <Clock className="h-4 w-4" /> Tiempo estimado de entrega
                </p>
                {tiempoAgotado ? (
                  <>
                    <p className="font-display font-black text-3xl text-white mt-3" data-testid="landing-timer">¡Ya casi!</p>
                    <p className="text-white/60 text-sm mt-1">Tu auto está por llegar a la bahía. Gracias por tu paciencia.</p>
                  </>
                ) : (
                  <>
                    <p className="font-display font-black text-[#F59E0B] leading-none mt-3" style={{ fontSize: "3.5rem" }} data-testid="landing-timer">
                      {fmtMMSS(remaining)}
                    </p>
                    <p className="text-white/50 text-xs mt-2 uppercase tracking-wider font-bold">Solicitud recibida · en preparación</p>
                  </>
                )}
              </motion.div>
            )}

            {listo && (
              <motion.div
                key="listo"
                initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                className="w-full border border-[#10B981]/40 bg-[#10B981]/10 p-6" data-testid="landing-listo-box"
              >
                <div className="text-center">
                  <CheckCircle2 className="h-12 w-12 text-[#10B981] mx-auto animate-pulse" />
                  <p className="font-display font-black text-2xl text-[#10B981] mt-3">¡Tu auto está listo!</p>
                  <p className="text-white/60 text-sm mt-1">Acércate a la bahía de salida para recogerlo.</p>
                </div>

                {confirmado ? (
                  <div className="mt-5 border-t border-white/10 pt-4 text-center" data-testid="landing-confirmado-box">
                    <ShieldCheck className="h-8 w-8 text-[#10B981] mx-auto" />
                    <p className="font-display font-black text-[#10B981] mt-2">Recepción confirmada</p>
                    <p className="text-white/50 text-xs mt-1">Registramos tu conformidad. ¡Buen viaje!</p>
                  </div>
                ) : (
                  <div className="mt-5 border-t border-white/10 pt-4" data-testid="landing-confirmacion">
                    <label className="flex items-start gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox" checked={aceptado} onChange={(e) => setAceptado(e.target.checked)}
                        data-testid="landing-confirmacion-check"
                        className="mt-1 h-5 w-5 shrink-0 accent-[#10B981]"
                      />
                      <span className="text-sm text-white/80 leading-snug">
                        Confirmo que recibo mi vehículo y pertenencias conforme al inventario.
                      </span>
                    </label>
                    <button
                      onClick={onConfirmar} disabled={!aceptado || confirmando}
                      data-testid="landing-confirmar-btn"
                      className={`btn-hard w-full h-14 mt-4 border font-black uppercase tracking-wider text-lg flex items-center justify-center gap-2 transition-opacity ${(!aceptado || confirmando) ? "bg-white/10 border-white/20 text-white/40 opacity-60 cursor-not-allowed" : "bg-[#10B981] text-[#04140C] border-[#10B981]"}`}
                    >
                      {confirmando ? <Loader2 className="h-6 w-6 animate-spin" /> : <><CheckCircle2 className="h-6 w-6" /> Confirmar recepción</>}
                    </button>
                    <p className="text-[11px] text-white/40 mt-2 text-center">Debes aceptar la conformidad para finalizar.</p>
                  </div>
                )}
              </motion.div>
            )}

            {entregado && (
              <motion.div
                key="entregado"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="w-full border border-white/15 bg-white/5 p-6 text-center" data-testid="landing-entregado-box"
              >
                <CheckCircle2 className="h-10 w-10 text-white/50 mx-auto" />
                <p className="font-display font-black text-xl text-white/80 mt-3">Vehículo entregado</p>
                <p className="text-white/50 text-sm mt-1">Gracias por confiar en nosotros. ¡Buen viaje!</p>
              </motion.div>
            )}
          </AnimatePresence>
        </section>

        {!entregado && (
          <section className="border border-white/10 bg-white/5 p-4" data-testid="landing-pago-aviso">
            <p className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-bold mb-3">Ten listo tu pago</p>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-2 text-sm font-bold text-white/80">
                <Banknote className="h-5 w-5 text-[#10B981]" /> Efectivo
              </span>
              {suc.acepta_tarjeta ? (
                <span className="inline-flex items-center gap-2 text-sm font-bold text-white/80">
                  <CreditCard className="h-5 w-5 text-[#3B82F6]" /> Tarjeta
                </span>
              ) : (
                <span className="text-xs text-[#F59E0B] font-bold uppercase tracking-wider">Solo efectivo</span>
              )}
            </div>
          </section>
        )}

        <UbicacionYContacto suc={suc} />
        {data?.contrato_adhesion && <ContratoAdhesion texto={data.contrato_adhesion} />}
      </main>

      <footer className="px-6 py-5 text-center border-t border-white/10">
        <p className="text-[10px] tracking-[0.2em] text-white/25 font-bold uppercase">Valet Parking · Actualización automática</p>
      </footer>
    </div>
  );
}

// ============================= Contenedor / data fetching =============================

export default function LandingPageCliente() {
  const { folio } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [errored, setErrored] = useState(false);
  const [solicitando, setSolicitando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [deadline, setDeadline] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const prevEstatusRef = useRef(null);
  const prevSirenRef = useRef(null); // Sprint 34.0 — evita repetir la sirena en cada polling

  const dlKey = `pap_valet_deadline_${folio}`;
  const esValet = data?.tipo_servicio === "VALET_PARKING";

  const fetchStatus = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/publico/ticket-status/${encodeURIComponent(folio)}`);
      setData(d);
      setNotFound(false);
      setErrored(false);
    } catch (err) {
      if (err.response?.status === 404) { setNotFound(true); setErrored(false); }
      else setErrored(true);
    } finally {
      setLoading(false);
    }
  }, [folio]);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 10000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  // Temporizador de entrega de Valet (persistente por folio)
  useEffect(() => {
    if (!data || data.tipo_servicio !== "VALET_PARKING") return;
    const est = data.estatus;
    if (est === "SOLICITADO") {
      let d = Number(localStorage.getItem(dlKey));
      if (!d || Number.isNaN(d)) {
        d = Date.now() + (Number(data.minutos_estimados_entrega) || 8) * 60000;
        localStorage.setItem(dlKey, String(d));
      }
      setDeadline(d);
    } else {
      localStorage.removeItem(dlKey);
      setDeadline(null);
    }
    prevEstatusRef.current = est;
  }, [data, dlKey]);

  useEffect(() => {
    if (!deadline) { setRemaining(0); return; }
    const tick = () => setRemaining(deadline - Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadline]);

  // Sprint 34.0 — Alerta sonora de alto impacto cuando el auto cambia a "En camino"/Retorno o "Listo en bahía"
  useEffect(() => {
    if (!data || data.tipo_servicio !== "VALET_PARKING") return;
    const est = data.estatus;
    const prev = prevSirenRef.current;
    if (prev && prev !== est && (est === "LISTO_EN_BAHIA" || est === "SOLICITADO")) {
      playSiren();
    }
    prevSirenRef.current = est;
  }, [data]);

  const solicitar = async () => {
    unlockAudio(); // gesto del usuario: habilita el audio para las alertas posteriores
    setSolicitando(true);
    try {
      const { data: res } = await api.post("/publico/solicitar-auto", { folio });
      const est = res?.estatus || "SOLICITADO";
      setData((prev) => (prev ? { ...prev, estatus: est } : prev));
      if (est === "SOLICITADO") {
        const d = Date.now() + (Number(data?.minutos_estimados_entrega) || 8) * 60000;
        localStorage.setItem(dlKey, String(d));
        setDeadline(d);
        toast.success("¡Solicitud enviada! Estamos trayendo tu auto.");
      } else if (est === "ENTREGADO") {
        toast("Tu vehículo ya fue entregado.");
      } else {
        toast("Estado actualizado.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo enviar la solicitud");
    } finally {
      setSolicitando(false);
    }
  };

  const confirmarRecepcion = async () => {
    setConfirmando(true);
    try {
      await api.post("/publico/confirmar-recepcion", { folio });
      setData((prev) => (prev ? { ...prev, confirmacion_cliente: true } : prev));
      toast.success("¡Gracias! Registramos tu conformidad de recepción.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo registrar la confirmación");
    } finally {
      setConfirmando(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#FFCC00]" data-testid="landing-loading" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center px-6 text-center" data-testid="landing-notfound">
        <div className="h-16 w-16 bg-[#EF4444]/15 border border-[#EF4444]/40 flex items-center justify-center mb-5">
          <AlertTriangle className="h-8 w-8 text-[#EF4444]" />
        </div>
        <h1 className="font-display text-2xl font-black tracking-tight">Folio no encontrado</h1>
        <p className="text-white/50 text-sm mt-2 max-w-xs">
          No pudimos localizar el folio <b className="text-white font-mono">{folio}</b>. Verifica el código QR de tu comprobante.
        </p>
      </div>
    );
  }

  if (errored && !data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center px-6 text-center" data-testid="landing-error">
        <div className="h-16 w-16 bg-[#F59E0B]/15 border border-[#F59E0B]/40 flex items-center justify-center mb-5">
          <Loader2 className="h-8 w-8 text-[#F59E0B] animate-spin" />
        </div>
        <h1 className="font-display text-2xl font-black tracking-tight">Obteniendo tu estatus…</h1>
        <p className="text-white/50 text-sm mt-2 max-w-xs">
          No pudimos conectar con el servicio. Reintentando automáticamente…
        </p>
      </div>
    );
  }

  if (esValet) {
    return (
      <VistaValet
        data={data}
        folio={folio}
        onSolicitar={solicitar}
        solicitando={solicitando}
        remaining={remaining}
        deadline={deadline}
        onConfirmar={confirmarRecepcion}
        confirmando={confirmando}
        confirmado={!!data?.confirmacion_cliente}
      />
    );
  }

  return <VistaAutoservicio data={data} />;
}
